import React, { useState, useEffect } from "react";
import {
  FiPhone,
  FiMapPin,
  FiTool,
  FiHardDrive,
  FiShield,
  FiClipboard,
  FiTruck,
  FiMenu,
  FiX,
  FiUser,
  FiLayout,
  FiChevronLeft,
  FiChevronRight,
  FiArrowRight,
  FiCheckCircle,
  FiHome,
  FiSettings,
  FiUserPlus,
  FiFileText,
  FiDollarSign,
  FiActivity,
  FiSliders
} from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import { motion, AnimatePresence } from "framer-motion";
import { blogPosts } from "./blogData";

// Framer Motion Animation Configs
const fadeInUpVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15 }
  }
};

export default function ShreeConstructionHPCL() {
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeSlide, setActiveSlide] = useState(0);
  const [activeTab, setActiveTab] = useState("all");
  
  // --- ADVANCED DUAL ESTIMATOR SYSTEM STATE ---
  const [calcType, setCalcType] = useState("construction");
  const [calcArea, setCalcArea] = useState(1500);
  const [customPricePerSqFt, setCustomPricePerSqFt] = useState(1650);

  // Configuration matrices for regional baseline rates
  const baseRatesMatrix = {
    construction: { name: "Full Civil Construction", defaultRate: 1650, min: 1400, max: 10000, label: "Structural Material + Labor Grade" },
    interior: { name: "Luxury Interior Setup", defaultRate: 950, min: 600, max: 6000, label: "Premium Hardware + Wood Grade" },
    painting: { name: "Architectural Painting", defaultRate: 250, min: 150, max: 2000, label: "Emulsion Quality + Putty Layers" },
    pump: { name: "Petroleum Fuel Station", defaultRate: 2400, min: 1900, max: 5500, label: "High-Load Driveway + Canopy Frame" },
    earthmover: { name: "Earthmoving & Excavation", defaultRate: 180, min: 120, max: 12000, label: "Heavy Machinery Trenching Depth" },
    infrastructure: { name: "Commercial Infrastructure", defaultRate: 2100, min: 700, max: 15000, label: "Reinforced Multi-Floor Framing" }
  };

  // Sync pricing parameters whenever type shifts
  useEffect(() => {
    setCustomPricePerSqFt(baseRatesMatrix[calcType].defaultRate);
  }, [calcType]);

  const carouselSlides = [
    {
      image: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1200&q=80",
      title: "Luxury Home Construction",
      tagline: "Building beautiful, strong lifelong houses with premium materials.",
    },
    {
      image: "https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?auto=format&fit=crop&w=1200&q=80",
      title: "Modern Interior Designing",
      tagline: "Creating breathtaking modular kitchens, smart rooms, and custom decor.",
    },
    {
      image: "https://images.unsplash.com/photo-1541535650810-10d26f5c2ab3?auto=format&fit=crop&w=1200&q=80",
      title: "Commercial Retail Spaces",
      tagline: "Approved developers for high-quality complexes, shopping bays, and corporate offices.",
    },
    {
      image: "https://images.unsplash.com/photo-1527018601619-a508a2be00cd?auto=format&fit=crop&w=1200&q=80",
      title: "Petroleum Infrastructure",
      tagline: "Class-A heavy retail contractors for high-capacity outlet driveway systems and canopies.",
    },
    {
      image: "https://images.unsplash.com/photo-1590069261209-f8e9b8642343?auto=format&fit=crop&w=1200&q=80",
      title: "Heavy Structural Engineering",
      tagline: "Delivering deep column foundations, premium steel framework layouts, and boundary clearances."
    }
  ];

  const projectPortfolio = [
    { id: 1, title: "Elite 4BHK Residential Villa", category: "residential", location: "Gomti Nagar, Lucknow", img: "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?auto=format&fit=crop&w=500&q=80" },
    { id: 2, title: "Premium Modular Kitchen & Wardrobes", category: "interior", location: "Hazratganj, Lucknow", img: "https://images.unsplash.com/photo-1556911220-e15b29be8c8f?auto=format&fit=crop&w=500&q=80" },
    { id: 3, title: "High-Capacity HPCL Retail Station", category: "infrastructure", location: "Mau Region, UP", img: "https://images.unsplash.com/photo-1527018601619-a508a2be00cd?auto=format&fit=crop&w=500&q=80" },
    { id: 4, title: "Multi-Floor Commercial Market Complex", category: "infrastructure", location: "Aliganj Portal, UP", img: "https://images.unsplash.com/photo-1541535650810-10d26f5c2ab3?auto=format&fit=crop&w=500&q=80" },
    { id: 5, title: "Luxury Living Room False Ceiling", category: "interior", location: "Indira Nagar, Lucknow", img: "https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?auto=format&fit=crop&w=500&q=80" },
    { id: 6, title: "Modern Duplex Structural Framework", category: "residential", location: "Vrindavan Yojna, Lucknow", img: "https://images.unsplash.com/photo-1513694203232-719a280e022f?auto=format&fit=crop&w=500&q=80" },
  ];

  useEffect(() => {
    const slideTimer = setInterval(() => {
      setActiveSlide((prev) => (prev === carouselSlides.length - 1 ? 0 : prev + 1));
    }, 6000);
    return () => clearInterval(slideTimer);
  }, [carouselSlides.length]);

  const nextSlide = () => setActiveSlide((prev) => (prev === carouselSlides.length - 1 ? 0 : prev + 1));
  const prevSlide = () => setActiveSlide((prev) => (prev === 0 ? carouselSlides.length - 1 : prev - 1));

  // Calculates valuation based on footprint multiplied by custom quality adjustments
  const calculateCombinedEstimate = () => {
    return (calcArea * customPricePerSqFt).toLocaleString("en-IN");
  };

  const filteredProjects = activeTab === "all" ? projectPortfolio : projectPortfolio.filter(p => p.category === activeTab);

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-900 scroll-smooth overflow-x-hidden">
      <Helmet>
        <title>Shree Construction | Complete Home Building, Interior Design & HPCL Infrastructure</title>
        <meta name="description" content="Shree Construction: Leading Class-A Contractor & Home Construction Expert. Specializing in complete house building, luxury interior design, modular setups, and commercial infrastructure in Lucknow, UP." />
      </Helmet>

      {/* --- Responsive Navigation --- */}
      <nav className="bg-white border-b-4 border-[#0054A6] sticky top-0 z-50 shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-[#0054A6] rounded-lg flex items-center justify-center text-white shadow-lg">
                <FiHome size={24} />
              </div>
              <div className="flex items-center">
                <span className="text-lg sm:text-xl font-black tracking-tight text-[#0054A6] leading-none uppercase">Shree</span>
                <span className="text-lg sm:text-xl pr-2 ml-1 font-black tracking-tight text-[#EE3124] leading-none uppercase">CONSTRUCTION</span>
              </div>
            </div>

            <div className="hidden md:flex items-center gap-6 text-sm font-bold text-slate-700">
              <a href="#services" className="hover:text-[#0054A6] transition-colors">Our Services</a>
              <a href="#portfolio" className="hover:text-[#0054A6] transition-colors">Live Projects</a>
              <a href="#workflow" className="hover:text-[#0054A6] transition-colors">Building Steps</a>
              <a href="#estimator" className="hover:text-[#0054A6] transition-colors">Cost Calculator</a>
              <a href="#enroll" className="hover:text-[#0054A6] transition-colors">Contact Us</a>
              <button onClick={() => navigate("/blog")} className="text-[#EE3124] hover:text-red-700 font-bold transition-all flex items-center gap-1">
                <FiFileText /> Industry Blog
              </button>
              <button 
    onClick={() => navigate("/login")} 
    className="px-4 py-2 rounded-md bg-[#0054A6] text-white hover:bg-blue-800 transition-all flex items-center gap-2 text-xs uppercase tracking-wider"
  >
    <FiUserPlus /> Partner Login
  </button>
            </div>

            <div className="md:hidden">
              <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="text-[#0054A6] p-2 focus:outline-none">
                {isMenuOpen ? <FiX size={28} /> : <FiMenu size={28} />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Sidebar Navigation */}
        {isMenuOpen && (
          <div className="md:hidden bg-white border-t border-slate-100 px-4 pt-2 pb-6 space-y-2 shadow-xl">
            <a href="#services" onClick={() => setIsMenuOpen(false)} className="block py-3 px-4 font-bold text-slate-700 border-b border-slate-50">Our Services</a>
            <a href="#portfolio" onClick={() => setIsMenuOpen(false)} className="block py-3 px-4 font-bold text-slate-700 border-b border-slate-50">Live Projects</a>
            <a href="#workflow" onClick={() => setIsMenuOpen(false)} className="block py-3 px-4 font-bold text-slate-700 border-b border-slate-50">Building Steps</a>
            <a href="#estimator" onClick={() => setIsMenuOpen(false)} className="block py-3 px-4 font-bold text-slate-700 border-b border-slate-50">Cost Calculator</a>
             <button 
    onClick={() => navigate("/login")}  
    className="px-4 py-2 rounded-md bg-[#0054A6] text-white hover:bg-blue-800 transition-all flex items-center gap-2 text-xs uppercase tracking-wider"
  >
    <FiUserPlus /> Partner Login
  </button>

            <button onClick={() => { setIsMenuOpen(false); navigate("/blog"); }} className="block w-full text-left py-3 px-4 font-bold text-[#EE3124]">
              Read Our Blog Hub
            </button>
          </div>
        )}
      </nav>

      {/* --- Premium Hero Section --- */}
      <header className="relative px-4 py-16 lg:py-28 text-white overflow-hidden bg-slate-950">
        <div className="absolute inset-0 z-0">
          <img src="https://images.unsplash.com/photo-1503387762-592deb58ef4e?auto=format&fit=crop&w=1600&q=80" alt="background" className="w-full h-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-900/90 to-transparent"></div>
        </div>

        <div className="relative max-w-7xl mx-auto grid lg:grid-cols-12 gap-12 items-center z-10">
          <motion.div 
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center lg:text-left lg:col-span-6"
          >
            <span className="inline-block px-4 py-1.5 rounded-full bg-[#EE3124] text-white text-[10px] font-extrabold uppercase tracking-widest mb-6 shadow-lg">
              Lucknow's Elite Structural Developers
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-tight mb-6 tracking-tight uppercase">
              Engineering <br />
              <span className="text-[#0054A6] bg-white px-2 py-1 rounded inline-block my-1">Strength</span> Realized.
            </h1>
            <p className="text-base sm:text-lg text-slate-300 mb-8 leading-relaxed max-w-xl mx-auto lg:mx-0">
              Approved Class-A governmental infrastructure contractors and premium home building consultants specialized in luxury residential layouts, corporate showrooms, and high-capacity petroleum sites.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <a href="#enroll" className="px-8 py-4 bg-[#EE3124] text-white rounded-md font-bold text-base shadow-xl hover:bg-red-700 transition-all flex items-center justify-center gap-2 uppercase tracking-wide">
                Consult Our Engineers <FiArrowRight />
              </a>
              <a href="#estimator" className="px-6 py-4 bg-white/10 hover:bg-white/20 border border-white/30 rounded-md font-bold text-sm text-white transition-all text-center">
                Calculate Structural Price
              </a>
            </div>
          </motion.div>

          {/* Interactive Carousel */}
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="lg:col-span-6 relative w-full h-[360px] sm:h-[440px] bg-slate-900 rounded-3xl overflow-hidden border border-white/10 shadow-2xl group"
          >
            <AnimatePresence mode="wait">
              <motion.img
                key={activeSlide}
                src={carouselSlides[activeSlide].image}
                alt={carouselSlides[activeSlide].title}
                initial={{ opacity: 0, filter: "blur(4px)" }}
                animate={{ opacity: 1, filter: "blur(0px)" }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.6 }}
                className="absolute inset-0 w-full h-full object-cover"
              />
            </AnimatePresence>
            <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/20 to-transparent"></div>

            <button onClick={prevSlide} className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/60 hover:bg-[#EE3124] text-white flex items-center justify-center z-20">
              <FiChevronLeft size={22} />
            </button>
            <button onClick={nextSlide} className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/60 hover:bg-[#EE3124] text-white flex items-center justify-center z-20">
              <FiChevronRight size={22} />
            </button>

            <div className="absolute bottom-0 left-0 w-full p-6 sm:p-10 text-left z-10">
              <span className="text-[9px] bg-[#0054A6] text-white font-black px-2.5 py-1 rounded uppercase tracking-widest mb-3 inline-block">Enterprise Portfolio</span>
              <h3 className="text-xl sm:text-3xl font-black text-white leading-none mb-2 uppercase drop-shadow-md">{carouselSlides[activeSlide].title}</h3>
              <p className="text-xs sm:text-sm text-slate-300 drop-shadow-sm font-medium">{carouselSlides[activeSlide].tagline}</p>
              
              <div className="flex gap-2 mt-5">
                {carouselSlides.map((_, index) => (
                  <button key={index} onClick={() => setActiveSlide(index)} className={`h-1.5 rounded-full transition-all duration-300 ${activeSlide === index ? "w-8 bg-[#EE3124]" : "w-2 bg-white/40"}`} />
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </header>

      {/* Metrics Strip */}
      <section className="px-6 py-12 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
          <StatItem count="500+" label="Properties Built Across UP" />
          <StatItem count="Class-A" label="Government Certified Rank" />
          <StatItem count="Zero Risk" label="100% RERA & Map Compliant" />
          <StatItem count="2022" label="Established Legacy of Trust" />
        </div>
      </section>

      {/* --- Core Services Grid --- */}
      <motion.section 
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={staggerContainer}
        id="services" 
        className="px-6 py-20 lg:py-28"
      >
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-xs font-black text-[#EE3124] tracking-widest uppercase">Expertise Capabilities</span>
            <h2 className="text-3xl sm:text-5xl font-black text-[#0054A6] mt-2 uppercase tracking-tight">Our Core Engineering Services</h2>
            <div className="h-1.5 w-24 bg-[#EE3124] mx-auto rounded-full mt-4"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <ServiceCard icon={<FiHome />} title="Turnkey House Construction" desc="Complete blueprint mapping, ground excavation, heavy structural iron column casting, premium brick masonry, and high-strength roof slab laying." image="https://images.unsplash.com/photo-1513694203232-719a280e022f?auto=format&fit=crop&w=500&q=80" />
            <ServiceCard icon={<FiLayout />} title="Luxury Modular Interiors" desc="Transforming inner layouts with water-proof marine plywood cabinets, soft-close hardware, custom profiles, false ceilings, and ambient architectural lighting." image="https://images.unsplash.com/photo-1556911220-e15b29be8c8f?auto=format&fit=crop&w=500&q=80" />
            <ServiceCard icon={<FiTruck />} title="Commercial Infrastructure" desc="Developing high-performance commercial showrooms, multi-floor retail shopping blocks, educational institute portals, and corporate hubs on frozen timelines." image="https://images.unsplash.com/photo-1541535650810-10d26f5c2ab3?auto=format&fit=crop&w=500&q=80" />
            <ServiceCard icon={<FiTool />} title="Petroleum Retail Outlets" desc="Specialized authorized installations for HPCL units, high-load concrete driveway grids, heavy canopy framing, fuel tank embedding, and technical testing panels." image="https://images.unsplash.com/photo-1527018601619-a508a2be00cd?auto=format&fit=crop&w=500&q=80" />
            <ServiceCard icon={<FiHardDrive />} title="Industrial Automation Routing" desc="Integrating advanced industrial distribution conduits, automated fueling panel systems, smart panel assemblies, and secure safety grounding networks." image="https://images.unsplash.com/photo-1558346490-a72e53ae2d4f?auto=format&fit=crop&w=500&q=80" />
            <ServiceCard icon={<FiShield />} title="Clearance & Approvals" desc="Managing site feasibility, municipal blueprint verification certificates, anti-fire department approvals, and coordination for legal hassle-free operation." image="https://images.unsplash.com/photo-1581094288338-2314dddb7ece?auto=format&fit=crop&w=500&q=80" />
          </div>
        </div>
      </motion.section>

      {/* --- Live Project Showcase --- */}
      <section id="portfolio" className="py-20 bg-slate-900 text-white px-6">
        <div className="max-w-7xl mx-auto text-center">
          <span className="text-xs font-black text-[#EE3124] tracking-widest uppercase">Verified Sight Inspections</span>
          <h2 className="text-3xl sm:text-4xl font-black uppercase mt-2 mb-8">Our Concrete Footprints</h2>
          
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            {["all", "residential", "interior", "infrastructure"].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-5 py-2.5 rounded font-black text-xs uppercase tracking-wider transition-all border ${activeTab === tab ? "bg-[#0054A6] border-[#0054A6] text-white" : "border-slate-700 text-slate-400 hover:text-white"}`}
              >
                {tab === "all" ? "Show All Projects" : `${tab} Works`}
              </button>
            ))}
          </div>

          <motion.div layout className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <AnimatePresence mode="popLayout">
              {filteredProjects.map((project) => (
                <motion.div
                  layout
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.4 }}
                  key={project.id}
                  className="bg-slate-800 rounded-xl overflow-hidden border border-slate-700 text-left group"
                >
                  <div className="h-56 overflow-hidden relative">
                    <img src={project.img} alt={project.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    <span className="absolute bottom-3 left-3 bg-[#EE3124] text-white font-black text-[9px] uppercase px-2 py-0.5 rounded tracking-widest">{project.category}</span>
                  </div>
                  <div className="p-5">
                    <h4 className="font-bold uppercase text-sm tracking-tight text-white mb-1">{project.title}</h4>
                    <p className="text-xs text-slate-400 flex items-center gap-1"><FiMapPin size={12} /> {project.location}</p>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>
        </div>
      </section>

      {/* --- Workflow Steps --- */}
      <section id="workflow" className="py-20 bg-slate-100 px-6 border-t border-b border-slate-200">
        <div className="max-w-6xl mx-auto text-center">
          <span className="text-xs font-black text-[#EE3124] uppercase tracking-widest block mb-2">Execution Protocol</span>
          <h2 className="text-3xl sm:text-5xl font-black text-[#0054A6] uppercase tracking-tight mb-4">Our Simple 4-Step Journey</h2>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-6 relative mt-12">
            <WorkflowStep stepNum="01" stepTitle="Plot Map & Design" stepDesc="We verify your plot boundary coordinates, test core soil hardness, and deliver legal 2D/3D blueprints." />
            <WorkflowStep stepNum="02" stepTitle="Budget & Material Lock" stepDesc="You pick standard brick grades, flooring tiles, and laminates. We lock the binding cost estimate." />
            <WorkflowStep stepNum="03" stepTitle="Solid Concrete Action" stepDesc="Our expert site engineering force completes iron column pouring, wall layout layering, and waterproofing." />
            <WorkflowStep stepNum="04" stepTitle="Luxury Handover" stepDesc="We secure modern modular finishes, false ceiling electrical routing, final painting work, and present keys." />
          </div>
        </div>
      </section>

      {/* --- REBUILT SECTION: SYNCHRONIZED DUAL-SLIDER COST ESTIMATOR --- */}
      <motion.section 
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={fadeInUpVariants}
        id="estimator" 
        className="py-20 bg-white px-6"
      >
        <div className="max-w-5xl mx-auto bg-gradient-to-br from-slate-900 via-[#011c3a] to-slate-950 text-white p-6 sm:p-12 rounded-3xl shadow-2xl border border-slate-800">
          <div className="text-center mb-10">
            <span className="text-xs font-black text-[#EE3124] bg-white/10 px-4 py-1.5 rounded-full uppercase tracking-widest flex items-center gap-2 w-fit mx-auto">
              <FiSliders /> Professional Multi-Domain Cost Planner
            </span>
            <h3 className="text-2xl sm:text-4xl font-black uppercase mt-4 tracking-tight">Estimate Your Project Parameters</h3>
            <p className="text-slate-400 text-xs sm:text-sm mt-2 max-w-xl mx-auto">
              Select your required field segment below, then scroll and adjust both footprint parameters and material rate metrics dynamically.
            </p>
          </div>

          {/* Expanded 6-Domain Selection Navigation */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 mb-10">
            {Object.keys(baseRatesMatrix).map((key) => (
              <button
                key={key}
                onClick={() => setCalcType(key)}
                className={`p-3.5 rounded-xl font-black text-[10px] uppercase tracking-wider transition-all border flex flex-col items-center justify-center gap-2 text-center ${calcType === key ? "bg-[#EE3124] border-[#EE3124] text-white shadow-xl scale-105" : "border-slate-800 bg-slate-900/50 text-slate-400 hover:text-white hover:border-slate-700"}`}
              >
                <span className="text-sm">
                  {key === "construction" && <FiHome />}
                  {key === "interior" && <FiLayout />}
                  {key === "painting" && <FiActivity />}
                  {key === "pump" && <FiTool />}
                  {key === "earthmover" && <FiTruck />}
                  {key === "infrastructure" && <FiHardDrive />}
                </span>
                <span>{baseRatesMatrix[key].name.split(" ")[0]}</span>
              </button>
            ))}
          </div>

          <div className="grid lg:grid-cols-12 gap-10 items-center">
            {/* Interactive Control Workspace */}
            <div className="lg:col-span-7 space-y-8 bg-slate-900/40 p-6 sm:p-8 rounded-2xl border border-slate-800/80">
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 flex items-center gap-1">
                    <FiSliders className="text-[#EE3124]" /> 1. Project Carpentry Area / Size
                  </label>
                  <span className="bg-slate-800 text-white font-mono text-xs font-black px-3 py-1 rounded border border-slate-700">
                    {calcArea.toLocaleString("en-IN")} Sq. Ft.
                  </span>
                </div>
                <input
                  type="range"
                  min="100"
                  max="25000"
                  step="50"
                  value={calcArea}
                  onChange={(e) => setCalcArea(Number(e.target.value))}
                  className="w-full accent-[#EE3124] bg-slate-800 h-2 rounded-lg cursor-pointer transition-all hover:bg-slate-700"
                />
                <div className="flex justify-between text-[9px] text-slate-500 font-black uppercase">
                  <span>100 Sq. Ft.</span>
                  <span>25,000 Sq. Ft.</span>
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 flex items-center gap-1">
                    <FiDollarSign className="text-emerald-500" /> 2. {baseRatesMatrix[calcType].label}
                  </label>
                  <span className="bg-slate-800 text-emerald-400 font-mono text-xs font-black px-3 py-1 rounded border border-slate-700">
                    ₹ {customPricePerSqFt} / Sq. Ft.
                  </span>
                </div>
                <input
                  type="range"
                  min={baseRatesMatrix[calcType].min}
                  max={baseRatesMatrix[calcType].max}
                  step="25"
                  value={customPricePerSqFt}
                  onChange={(e) => setCustomPricePerSqFt(Number(e.target.value))}
                  className="w-full accent-emerald-500 bg-slate-800 h-2 rounded-lg cursor-pointer transition-all hover:bg-slate-700"
                />
                <div className="flex justify-between text-[9px] text-slate-500 font-black uppercase">
                  <span>₹ {baseRatesMatrix[calcType].min} Min Base</span>
                  <span>₹ {baseRatesMatrix[calcType].max} Premium Grade</span>
                </div>
              </div>
            </div>

            {/* Output Calculation Analytics Card */}
            <div className="lg:col-span-5 bg-gradient-to-b from-[#01254a] to-[#001730] p-6 sm:p-8 rounded-2xl border border-blue-900/50 text-center lg:text-left flex flex-col justify-between h-full shadow-xl relative overflow-hidden group">
              <div className="absolute top-0 right-0 p-8 opacity-5 text-white pointer-events-none">
                <FiDollarSign size={140} />
              </div>
              <div className="relative z-10">
                <span className="text-[9px] font-black tracking-widest text-blue-300 block uppercase bg-blue-950/80 px-2.5 py-1 rounded w-fit mx-auto lg:mx-0 border border-blue-900">
                  Indicative Budget Summary
                </span>
                <p className="text-slate-400 text-xs mt-4 font-bold uppercase tracking-wide">
                  Selected Segment: <span className="text-white block text-sm font-black tracking-normal mt-0.5 uppercase">{baseRatesMatrix[calcType].name}</span>
                </p>
                
                <div className="my-6 border-y border-blue-900/60 py-4">
                  <span className="text-[9px] font-black tracking-widest text-slate-400 block uppercase">Estimated Total Cost Valuation</span>
                  <div className="text-2xl sm:text-3xl lg:text-4xl font-black text-white mt-1 flex items-center justify-center lg:justify-start gap-1 tracking-tight">
                    <span className="text-[#EE3124]">₹</span>
                    <span>{calculateCombinedEstimate()} /-</span>
                  </div>
                </div>
              </div>

              <div className="space-y-3 relative z-10">
                <p className="text-[10px] text-slate-400 leading-relaxed italic text-left">
                  *Calculated parameters scale according to regional averages in Uttar Pradesh. Material shifts (e.g. brick grading, modular laminate thicknesses) will adjust these metrics.
                </p>
                <a href="#enroll" className="block text-center py-3.5 bg-[#EE3124] text-white font-black text-xs uppercase rounded-lg tracking-wider shadow-lg hover:bg-red-700 transition-all active:scale-95">
                  Request Breakdown Sheet
                </a>
              </div>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Helpful Articles Hub */}
      <section className="px-6 py-20 bg-slate-50 border-t border-slate-200">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col sm:flex-row justify-between items-center mb-12 gap-4">
            <div>
              <h3 className="text-2xl sm:text-3xl font-black text-[#0054A6] uppercase tracking-tight">Helpful Engineering Logs</h3>
              <p className="text-slate-500 text-xs sm:text-sm mt-1">Read standard structural safety guidelines drafted by our corporate desk.</p>
            </div>
            <button onClick={() => navigate("/blog")} className="px-5 py-2.5 bg-[#0054A6] text-white rounded text-xs font-black uppercase tracking-wider flex items-center gap-1.5 transition-all shadow-md">
              Open Full Blog Hub <FiArrowRight />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {blogPosts.slice(0, 3).map((post) => (
              <div key={post.id} className="bg-white rounded-xl border border-slate-200 overflow-hidden p-6 flex flex-col justify-between hover:shadow-xl transition-all">
                <div>
                  <span className="text-[10px] text-[#EE3124] font-black uppercase tracking-widest block mb-2">{post.category}</span>
                  <h4 className="font-bold text-slate-900 text-sm uppercase tracking-tight mb-2 line-clamp-2">{post.title}</h4>
                  <p className="text-slate-500 text-xs line-clamp-3 leading-relaxed mb-4">{post.summary}</p>
                </div>
                <button onClick={() => navigate("/blog")} className="text-left text-xs font-black text-[#0054A6] hover:text-[#EE3124] uppercase tracking-wider flex items-center gap-1 transition-colors">
                  Read Full Article Details →
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Client Feedback Section */}
      <section className="py-20 bg-white border-t border-slate-200 px-6">
        <div className="max-w-6xl mx-auto text-center">
          <span className="text-xs font-black text-[#EE3124] tracking-widest uppercase">Validated Endorsements</span>
          <h2 className="text-3xl sm:text-4xl font-black uppercase mt-2 mb-12 text-[#0054A6]">Client Satisfaction Stories</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-left">
            <div className="p-6 bg-slate-50 rounded-2xl border border-slate-100">
              <p className="text-slate-600 text-xs leading-relaxed italic">"Shree Construction handled our 3-story house framework deployment in Gomti Nagar. The raw material grades used matched our contract parameters perfectly. Very straightforward management team."</p>
              <h5 className="font-bold text-slate-900 text-xs uppercase tracking-wider mt-4">— Anand Verma, Property Owner</h5>
            </div>
            <div className="p-6 bg-slate-50 rounded-2xl border border-slate-100">
              <p className="text-slate-600 text-xs leading-relaxed italic">"We partnered with them for commercial layout design and HPCL petroleum retail driveway block installations. Passed government compliance inspections smoothly without any operational delays."</p>
              <h5 className="font-bold text-slate-900 text-xs uppercase tracking-wider mt-4">— Institutional Site Director, UP</h5>
            </div>
            <div className="p-6 bg-slate-50 rounded-2xl border border-slate-100">
              <p className="text-slate-600 text-xs leading-relaxed italic">"Excellent interior execution style. Our modern modular kitchen pulls, custom false ceilings, and premium living room laminate finishes look incredibly premium."</p>
              <h5 className="font-bold text-slate-900 text-xs uppercase tracking-wider mt-4">— S. Srivastava, Lucknow Flat Owner</h5>
            </div>
          </div>
        </div>
      </section>

      {/* Form Submission Block */}
      <section id="enroll" className="px-4 py-20 bg-slate-100 border-t border-slate-200">
        <div className="max-w-5xl mx-auto">
          <div className="bg-white rounded-3xl shadow-xl overflow-hidden grid lg:grid-cols-5 border-t-8 border-[#0054A6]">
            <div className="lg:col-span-2 bg-[#0054A6] p-8 lg:p-12 text-white">
              <h2 className="text-2xl lg:text-3xl font-black mb-6 uppercase tracking-tight">Request Estimate</h2>
              <p className="text-blue-100 mb-8 text-sm leading-relaxed">Connect with our corporate office directly to book on-site land survey evaluations or secure interior layout catalog access sheets.</p>
              <div className="space-y-4 text-xs font-bold uppercase tracking-wide">
                <div className="flex items-center gap-3"><FiCheckCircle className="text-[#EE3124]" /> Expert Area Architecture</div>
                <div className="flex items-center gap-3"><FiCheckCircle className="text-[#EE3124]" /> Branded Marine Wood Materials</div>
                <div className="flex items-center gap-3"><FiCheckCircle className="text-[#EE3124]" /> Clear Legal Documentation</div>
              </div>
            </div>

            <form action="https://api.web3forms.com/submit" method="POST" className="lg:col-span-3 p-6 sm:p-12 space-y-6 bg-white">
              <input type="hidden" name="access_key" value="20408c94-1599-4f8d-9cc3-cc6a68c6b514" />
              <div className="grid sm:grid-cols-2 gap-6">
                <InputGroup icon={<FiUser />} label="Full Name">
                  <input required name="name" className="w-full bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm focus:ring-2 focus:ring-[#0054A6] outline-none text-slate-800 font-medium" placeholder="e.g. Rajesh Kumar" />
                </InputGroup>
                <InputGroup icon={<FiPhone />} label="Primary Phone Number">
                  <input required type="tel" name="phone" className="w-full bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm focus:ring-2 focus:ring-[#0054A6] outline-none text-slate-800 font-medium" placeholder="10-digit mobile" />
                </InputGroup>
              </div>
              <InputGroup icon={<FiSettings />} label="Required Category">
                <select name="service" className="w-full bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm focus:ring-2 focus:ring-[#0054A6] outline-none font-bold text-slate-700 cursor-pointer">
                  <option>Full House Construction (Villas / Homes)</option>
                  <option>Premium Interior Designing & Decor</option>
                  <option>Commercial Complex / Building Work</option>
                  <option>Petrol Pump Driveway & Construction</option>
                </select>
              </InputGroup>
              <InputGroup icon={<FiMapPin />} label="Project Plot Address Location">
                <input required name="address" placeholder="e.g. Gomti Nagar, Lucknow, UP" className="w-full bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm focus:ring-2 focus:ring-[#0054A6] outline-none text-slate-800 font-medium" />
              </InputGroup>
              <InputGroup icon={<FiClipboard />} label="Project Configuration Scope">
                <textarea name="message" rows="4" placeholder="Mention plot measurements, targeted room counts, and budget parameters here..." className="w-full bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm focus:ring-2 focus:ring-[#0054A6] outline-none text-slate-800 font-medium" />
              </InputGroup>
              <button type="submit" className="w-full py-4 bg-[#EE3124] text-white rounded-lg font-black uppercase tracking-widest text-xs hover:bg-red-700 transition-all shadow-lg">
                Submit Specifications Data
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Main Footer Block */}
      <footer className="bg-slate-900 text-white py-12 px-6">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-10">
          <div>
            <h5 className="font-bold text-[#EE3124] uppercase mb-4 tracking-wider">Shree Construction</h5>
            <p className="text-slate-400 text-xs leading-relaxed">Delivering lifelong structural stability, commercial building deployment setups, and high-tier interior engineering across the Uttar Pradesh region.</p>
          </div>
          <div>
            <h5 className="font-bold text-white uppercase mb-4 tracking-wider">Headquarters Base</h5>
            <p className="text-slate-400 text-xs flex items-center gap-1.5"><FiMapPin size={12} /> Lucknow, Uttar Pradesh, India</p>
            <p className="text-slate-400 text-xs mt-2">Email: shreeconstruction0004@gmail.com</p>
            <p className="text-[#EE3124] font-black mt-3 text-lg tracking-wide">+91 6387501837</p>
          </div>
          <div className="flex flex-col md:items-end text-xs text-slate-400 font-bold uppercase tracking-wider">
            <h5 className="font-bold text-white uppercase mb-4 tracking-wider">Quick Navigation Links</h5>
            <button onClick={() => navigate('/blog')} className="hover:text-white transition-colors block mb-2">Open Knowledge Hub</button>
          </div>
        </div>
      </footer>
    </div>
  );
}

// Custom Render Sub-components
function ServiceCard({ icon, title, desc, image }) {
  return (
    <motion.div 
      variants={fadeInUpVariants}
      whileHover={{ y: -6 }}
      transition={{ duration: 0.3 }}
      className="group relative overflow-hidden p-6 sm:p-8 bg-white border-b-4 border-slate-200 hover:border-[#EE3124] shadow-md rounded-2xl text-left"
    >
      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-all duration-500 z-0">
        <img src={image} alt={title} className="w-full h-full object-cover scale-105 group-hover:scale-100 transition-transform duration-500" />
        <div className="absolute inset-0 bg-slate-950/85"></div>
      </div>
      <div className="relative z-10">
        <div className="w-12 h-12 bg-slate-100 text-[#0054A6] rounded-xl flex items-center justify-center text-2xl mb-6 font-bold group-hover:bg-white transition-colors">{icon}</div>
        <h3 className="text-lg font-black mb-3 text-slate-800 group-hover:text-white uppercase tracking-tight">{title}</h3>
        <p className="text-slate-500 group-hover:text-slate-300 text-xs leading-relaxed">{desc}</p>
      </div>
    </motion.div>
  );
}

function StatItem({ count, label }) {
  return (
    <div className="p-3 bg-slate-50 rounded-xl border border-slate-100">
      <h4 className="text-3xl font-black text-[#0054A6] tracking-tight">{count}</h4>
      <p className="text-slate-400 text-[10px] font-black uppercase tracking-widest mt-1 leading-none">{label}</p>
    </div>
  );
}

function InputGroup({ icon, label, children }) {
  return (
    <div className="space-y-2 text-left">
      <label className="text-[9px] uppercase tracking-widest font-black text-slate-400 flex items-center gap-1.5">{icon} {label}</label>
      {children}
    </div>
  );
}

function WorkflowStep({ stepNum, stepTitle, stepDesc }) {
  return (
    <div className="bg-white p-6 rounded-2xl border border-slate-200 text-left shadow-md relative group hover:border-[#0054A6] transition-colors">
      <span className="text-4xl font-black text-slate-100 group-hover:text-[#EE3124] block mb-2 transition-colors">{stepNum}</span>
      <h4 className="font-bold text-slate-900 text-sm uppercase tracking-tight mb-2">{stepTitle}</h4>
      <p className="text-slate-500 text-xs leading-relaxed">{stepDesc}</p>
    </div>
  );
}